import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { DSIcon, DSInput, DSButton } from '../design-system/primitives';

function ClaimAccount() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const validateToken = useCallback(async () => {
    setValidating(true);
    try {
      const response = await apiService.validateClaimToken(token);
      if (response.data.valid) {
        setTokenValid(true);
        setUserInfo(response.data);
        setFirstName(response.data.firstName || '');
        setLastName(response.data.lastName || '');
      } else {
        setError('Invalid or expired claim token');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to validate token');
    } finally {
      setLoading(false);
      setValidating(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('No claim token provided');
      return;
    }

    validateToken();
  }, [token, validateToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await apiService.claimAccount({
        token,
        password,
        firstName,
        lastName
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to claim account');
    }
  };

  if (loading || validating) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h2>Validating...</h2>
          <p>Please wait while we validate your claim token.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}><DSIcon name="sparkles" /></div>
            <h2>Account Claimed Successfully!</h2>
            <p>Your account is now set up and ready to use.</p>
            <p>You can now log in with your email and the password you just created.</p>
            <Link to="/login" className="button button-primary" style={{ marginTop: '20px', display: 'inline-block' }}>
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}><DSIcon name="warning" /></div>
            <h2>Invalid or Expired Token</h2>
            <p>{error || 'This claim link is no longer valid.'}</p>
            <p>If you need a new claim link, please try registering again or contact support.</p>
            <Link to="/login" className="button button-secondary" style={{ marginTop: '20px', display: 'inline-block' }}>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2>Claim Your Account</h2>
        <p style={{ marginBottom: '20px', color: 'var(--ds-color-textMuted)' }}>
          An account was created for <strong>{userInfo?.email}</strong> from your Square payment history.
          Set up your password to complete your account.
        </p>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <DSInput label="Email" 
              type="email" 
              value={userInfo?.email || ''} 
              disabled
              style={{ backgroundColor: 'var(--ds-color-surfaceRaised)' }} />

          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <DSInput label="First Name" 
                type="text" 
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name" />
            <DSInput label="Last Name" 
                type="text" 
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name" />
          </div>

          <DSInput label="Password" 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min 8 characters)"
              required
              minLength={8} />

          <DSInput label="Confirm Password" 
              type="password" 
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required />

          <DSButton tone="primary" appearance="solid" type="submit" style={{ width: '100%' }}>
            Claim My Account
          </DSButton>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link to="/login" style={{ color: 'var(--ds-color-statusInfoText)' }}>Already have access? Login</Link>
        </div>
      </div>
    </div>
  );
}

export default ClaimAccount;
